import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ImageIcon, Grid, List, ArrowLeft, Heart, Download, Share2, Eye, Calendar, ExternalLink, MapPin, Instagram, Globe, Music } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ShareModal from '../components/ShareModal';
import DownloadPaywall from '../components/DownloadPaywall';
import SignupInviteModal from '../components/SignupInviteModal';

const ArtistGallery = () => {
    const { username } = useParams();
    const { isAuthenticated } = useAuth();
    const [artist, setArtist] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [shareModal, setShareModal] = useState({ isOpen: false, image: null });
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, image: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'image' });

    useEffect(() => {
        fetchArtistData();
        fetchArtistImages();
    }, [username]);

    // Update page metadata
    useEffect(() => {
        if (artist) {
            const artistName = artist.displayName || artist.username;
            const title = `${artistName} - Artwork Gallery`;
            const description = `Discover amazing artwork and photos by ${artistName} on The Deep Wave. Browse their complete gallery of ${images.length} creative images including digital art, photography, and visual content.`;
            const keywords = `${artistName}, artwork, gallery, digital art, photography, visual art, creative images, the deep wave, ${artist.username}`;
            
            // Update page title
            document.title = `${title} | The Deep Wave`;
            
            // Update meta description
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.content = description;
            }

            // Update or create keywords meta tag
            updateMetaTag('keywords', keywords, 'name');

            // Update Open Graph tags
            updateMetaTag('og:title', `${title} | The Deep Wave`);
            updateMetaTag('og:description', `View ${images.length} pieces of artwork by ${artistName} on The Deep Wave`);
            updateMetaTag('og:url', window.location.href);
            updateMetaTag('og:type', 'profile');
            updateMetaTag('og:site_name', 'The Deep Wave');
            
            // Use artist profile image or fallback
            const ogImageUrl = artist.profilePictureUrl || 'https://thedeepwave.com/og-image.jpg';
            updateMetaTag('og:image', ogImageUrl);
            updateMetaTag('og:image:alt', `${artistName} - Artist Gallery on The Deep Wave`);

            // Twitter Card tags
            updateMetaTag('twitter:card', 'summary_large_image', 'name');
            updateMetaTag('twitter:title', `${title} | The Deep Wave`, 'name');
            updateMetaTag('twitter:description', `View artwork by ${artistName} on The Deep Wave`, 'name');
            updateMetaTag('twitter:image', ogImageUrl, 'name');

            // Additional profile-specific meta tags
            updateMetaTag('profile:username', artist.username);
            if (artist.displayName) {
                updateMetaTag('profile:first_name', artist.displayName);
            }

            // Additional SEO tags
            updateMetaTag('robots', 'index, follow', 'name');
            updateMetaTag('author', artistName, 'name');
            updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', 'name');
        }
    }, [artist, images.length]);

    const updateMetaTag = (property, content, type = 'property') => {
        let selector = type === 'name' ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        let tag = document.querySelector(selector);
        
        if (!tag) {
            tag = document.createElement('meta');
            if (type === 'name') {
                tag.name = property;
            } else {
                tag.property = property;
            }
            document.head.appendChild(tag);
        }
        tag.content = content;
    };

    const fetchArtistData = async () => {
        try {
            const response = await fetch(`/api/profile/${username}`);
            const result = await response.json();
            
            if (result.success) {
                setArtist(result.profile || result.data);
            } else {
                setError('Artist not found');
            }
        } catch (err) {
            console.error('Error fetching artist:', err);
            setError('Artist not found');
        }
    };

    const fetchArtistImages = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/upload/public/image?limit=100');
            
            if (response.data.success) {
                // Filter for only this artist's images
                const artistImages = response.data.data.filter(img => 
                    img.username === username || 
                    (img.display_name && img.display_name.toLowerCase() === username.toLowerCase())
                );
                
                setImages(artistImages);
            } else {
                setError('Failed to load images');
            }
        } catch (err) {
            console.error('Error fetching images:', err);
            setError('Unable to load gallery. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleShareClick = (image) => {
        setShareModal({ isOpen: true, image });
    };

    const handleDownloadClick = (image) => {
        setDownloadPaywall({ isOpen: true, image });
    };

    const handleLike = async (image) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'image' });
            return;
        }
        try {
            const response = await api.post(`/upload/${image.id}/like`);
            if (response.data.success) {
                setImages(prev => prev.map(img =>
                    img.id === image.id
                        ? { ...img, user_has_liked: response.data.data.liked, like_count: response.data.data.total_likes }
                        : img
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const then = new Date(dateString);
        const diffInHours = Math.floor((now - then) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
        
        const diffInMonths = Math.floor(diffInDays / 30);
        return `${diffInMonths}mo ago`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = (bytes / (1024 * 1024)).toFixed(1);
        return `${mb} MB`;
    };

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4">🎨</div>
                    <h1 className="text-2xl font-bold text-white mb-2">Artist Not Found</h1>
                    <p className="text-gray-400 mb-6">The artist you're looking for doesn't exist or has no public gallery.</p>
                    <Link
                        to="/gallery"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Gallery
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            {artist && (
                <PageHeader 
                    title={`${artist.displayName || artist.username}'s Gallery`}
                    subtitle={`Artwork and photos by ${artist.displayName || artist.username} • ${images.length} images`}
                    icon={<ImageIcon className="w-8 h-8" />}
                />
            )}

            {/* Artist Info Banner */}
            {artist && (
                <div className="relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-blue-900/30 to-pink-900/40"></div>
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJtIDYwIDAgTCAwIDAgMCA2MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
                    
                    {/* Content */}
                    <div className="relative">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                            <div className="flex flex-col lg:flex-row items-start gap-6 lg:gap-8">
                                {/* Artist Avatar */}
                                <div className="flex-shrink-0">
                                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                                        {artist.profilePictureUrl ? (
                                            <img
                                                src={artist.profilePictureUrl}
                                                alt={artist.displayName || artist.username}
                                                width="96"
                                                height="96"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    // Fallback to gradient avatar if image fails
                                                    const fallback = document.createElement('div');
                                                    fallback.className = "w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center";
                                                    fallback.innerHTML = `<span class="text-white text-2xl sm:text-4xl font-bold">${(artist.displayName || artist.username)?.charAt(0) || 'A'}</span>`;
                                                    e.target.parentNode.innerHTML = fallback.outerHTML;
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                                <span className="text-white text-2xl sm:text-4xl font-bold">
                                                    {(artist.displayName || artist.username)?.charAt(0) || 'A'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Artist Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            {/* Name and Username */}
                                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 leading-tight">
                                                {artist.displayName || artist.username}
                                            </h1>
                                            <p className="text-lg text-purple-200 mb-3 flex items-center gap-2">
                                                <span>🌊</span>
                                                @{artist.username}
                                            </p>

                                            {/* Bio */}
                                            {artist.bio && (
                                                <p className="text-gray-300 leading-relaxed mb-4 max-w-3xl">
                                                    {artist.bio}
                                                </p>
                                            )}

                                            {/* Stats */}
                                            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-4">
                                                <div className="text-center">
                                                    <div className="text-xl sm:text-2xl font-bold text-white">{images.length}</div>
                                                    <div className="text-xs sm:text-sm text-gray-400">Images</div>
                                                </div>
                                                {artist.follower_count !== undefined && (
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-white">{artist.follower_count || 0}</div>
                                                        <div className="text-xs sm:text-sm text-gray-400">Followers</div>
                                                    </div>
                                                )}
                                                {artist.upload_count !== undefined && (
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-white">{artist.upload_count || 0}</div>
                                                        <div className="text-xs sm:text-sm text-gray-400">Total Uploads</div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Location */}
                                            {artist.location && (
                                                <p className="text-gray-400 text-sm flex items-center gap-2 mb-3">
                                                    <MapPin className="w-4 h-4" />
                                                    {artist.location}
                                                </p>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-3 sm:flex-shrink-0">
                                            <Link
                                                to={`/profile/${artist.username}`}
                                                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-lg hover:shadow-xl transform hover:scale-105 duration-200"
                                            >
                                                <span>🌊</span>
                                                View Profile
                                            </Link>
                                            
                                            <Link
                                                to="/gallery"
                                                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                            >
                                                <ArrowLeft className="w-4 h-4" />
                                                All Artists
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Social Links */}
                                    {(artist.website || artist.instagram || artist.social_links) && (
                                        <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-white/10">
                                            {artist.website && (
                                                <a
                                                    href={artist.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
                                                >
                                                    <Globe className="w-4 h-4" />
                                                    Website
                                                </a>
                                            )}
                                            {artist.instagram && (
                                                <a
                                                    href={`https://instagram.com/${artist.instagram.replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3 py-2 bg-pink-600/20 hover:bg-pink-600/30 text-pink-200 rounded-lg transition-colors text-sm"
                                                >
                                                    <Instagram className="w-4 h-4" />
                                                    Instagram
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-8 sm:pb-12">
                {/* View Controls */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-400">
                            {images.length} image{images.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'grid' 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                        >
                            <Grid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'list' 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="aspect-square bg-gray-800 rounded-lg animate-pulse" />
                        ))}
                    </div>
                ) : images.length === 0 ? (
                    <div className="text-center py-16">
                        <ImageIcon className="w-24 h-24 text-gray-600 mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-white mb-2">No Images Yet</h2>
                        <p className="text-gray-400 mb-6">
                            {artist?.displayName || artist?.username || 'This artist'} hasn't shared any images yet.
                        </p>
                        <Link
                            to="/gallery"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            Browse All Artists
                        </Link>
                    </div>
                ) : (
                    <div className={
                        viewMode === 'grid'
                            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'
                            : 'space-y-3 sm:space-y-4'
                    }>
                        {images.map((image) => (
                            <div
                                key={image.id}
                                className={
                                    viewMode === 'grid'
                                        ? 'glass-card overflow-hidden hover:scale-105 transition-all duration-300 group'
                                        : 'glass-card p-4 flex gap-4 hover:bg-gray-800/50 transition-all duration-300 group'
                                }
                            >
                                {viewMode === 'grid' ? (
                                    <>
                                        {/* Grid View */}
                                        <div className="relative aspect-square bg-gray-900 overflow-hidden">
                                            <img
                                                src={image.optimized_url || image.file_url}
                                                alt={image.title || image.original_filename}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />

                                            {/* Overlay Controls */}
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleLike(image)}
                                                        className={`p-2 rounded-lg transition-colors ${
                                                            image.user_has_liked
                                                                ? 'bg-red-500/80 hover:bg-red-600'
                                                                : 'bg-gray-500/80 hover:bg-red-500/80'
                                                        }`}
                                                        title={isAuthenticated ? (image.user_has_liked ? 'Liked' : 'Like') : 'Sign up to like'}
                                                    >
                                                        <Heart className={`w-4 h-4 text-white ${image.user_has_liked ? 'fill-current' : ''}`} />
                                                    </button>
                                                    <Link
                                                        to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                                        className="p-2 bg-blue-500/80 rounded-lg hover:bg-blue-600 transition-colors"
                                                        title="View Full Size"
                                                    >
                                                        <Eye className="w-4 h-4 text-white" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleShareClick(image)}
                                                        className="p-2 bg-purple-500/80 rounded-lg hover:bg-purple-600 transition-colors"
                                                        title="Share"
                                                    >
                                                        <Share2 className="w-4 h-4 text-white" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadClick(image)}
                                                        className="p-2 bg-green-500/80 rounded-lg hover:bg-green-600 transition-colors"
                                                        title="Download"
                                                    >
                                                        <Download className="w-4 h-4 text-white" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* File Size Badge */}
                                            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                {formatFileSize(image.file_size)}
                                            </div>
                                        </div>

                                        {/* Image Info */}
                                        <div className="p-4">
                                            <h3 className="font-semibold text-white text-sm mb-1 truncate">
                                                {image.title || image.original_filename}
                                            </h3>
                                            <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                                                <span>🌊</span>
                                                <Link
                                                    to={`/profile/${image.username}`}
                                                    className="hover:text-blue-400 transition-colors truncate"
                                                >
                                                    {image.artist || image.display_name || image.username}
                                                </Link>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{image.medium || 'Digital'}</span>
                                                <div className="flex items-center gap-1">
                                                    <Eye className="w-3 h-3" />
                                                    <span>{(image.view_count || image.play_count || 0).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* List View */}
                                        <div className="w-20 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={image.optimized_url || image.file_url}
                                                alt={image.title || image.original_filename}
                                                width="80"
                                                height="80"
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-white mb-1 truncate">
                                                        {image.title || image.original_filename}
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                                        <Link
                                                            to={`/profile/${image.username}`}
                                                            className="hover:text-blue-400 transition-colors"
                                                        >
                                                            {image.artist || image.display_name || image.username}
                                                        </Link>
                                                        <span>•</span>
                                                        <span>{formatTimeAgo(image.created_at)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                                        <span>{image.medium || 'Digital'}</span>
                                                        <span>{image.category || 'Artwork'}</span>
                                                        <span>{formatFileSize(image.file_size)}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1 ml-4">
                                                    <button
                                                        onClick={() => handleLike(image)}
                                                        className={`p-2 transition-colors ${
                                                            image.user_has_liked
                                                                ? 'text-red-400 hover:text-red-300'
                                                                : 'text-gray-400 hover:text-red-400'
                                                        }`}
                                                        title={isAuthenticated ? (image.user_has_liked ? 'Liked' : 'Like') : 'Sign up to like'}
                                                    >
                                                        <Heart className={`w-4 h-4 ${image.user_has_liked ? 'fill-current' : ''}`} />
                                                    </button>
                                                    <Link
                                                        to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                        title="View"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleShareClick(image)}
                                                        className="p-2 text-gray-400 hover:text-purple-400 transition-colors"
                                                        title="Share"
                                                    >
                                                        <Share2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadClick(image)}
                                                        className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                                                        title="Download"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                    <div className="flex items-center gap-1 text-xs text-gray-500 ml-2">
                                                        <Eye className="w-3 h-3" />
                                                        <span>{(image.view_count || image.play_count || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, image: null })}
                mediaTitle={shareModal.image?.title || 'Artwork'}
                mediaType="image"
                username={username}
                mediaId={shareModal.image?.stored_filename || shareModal.image?.original_filename || shareModal.image?.id}
                artistName={artist?.displayName || artist?.username}
            />

            {/* Download Paywall */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, image: null })}
                mediaTitle={downloadPaywall.image?.title || 'Artwork'}
                mediaType="image"
                uploadId={downloadPaywall.image?.id}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'image' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default ArtistGallery;